import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Pagination } from '@/components/common/Pagination';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { EXPENSE_STATUS_VALUES } from '../types';
import type { Expense, ExpenseFilters, ExpenseStatus } from '../types';
import { useExpenseColumns } from './ExpenseColumns';

interface ExpensesDataTableProps {
  data: Expense[];
  total: number;
  filters: ExpenseFilters;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  onFiltersChange: <K extends keyof ExpenseFilters>(key: K, value: ExpenseFilters[K]) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onClearFilters?: (() => void) | undefined;
  onEdit?: ((expense: Expense) => void) | undefined;
  onCreate?: (() => void) | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
}

export function ExpensesDataTable({
  data,
  total,
  filters,
  isLoading,
  isError,
  onFiltersChange,
  onPageChange,
  onLimitChange,
  onClearFilters,
  onEdit,
  onCreate,
  hasAccess,
  controlsContainer,
}: ExpensesDataTableProps) {
  const { t } = useTranslation();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('expenses');
  const tableZoom = useTableZoom();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [rowSelection, setRowSelection] = React.useState({});

  const columns = useExpenseColumns({
    onEdit,
    hasAccess,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { columnVisibility, rowSelection },
  });

  const currentPage = filters.page ?? 0;
  const pageSize = filters.limit ?? 20;

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.expenseCategoryId) count++;
    if (filters.matterId) count++;
    if (filters.contractId) count++;
    return count;
  }, [filters]);

  return (
    <div className="w-full space-y-4">
      {/* Status toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        className="justify-start gap-1"
        value={filters.status ?? 'none'}
        onValueChange={(item) =>
          onFiltersChange('status', item === 'none' ? undefined : (item as ExpenseStatus))
        }
      >
        <ToggleGroupItem
          value="none"
          aria-label="All"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          {t('all')}
        </ToggleGroupItem>
        {EXPENSE_STATUS_VALUES.map((s) => (
          <ToggleGroupItem
            key={s}
            value={s}
            aria-label={s}
            className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
          >
            {t(`expense.status.${s}`)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Actions bar */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          {total > 0 && (
            <span>
              {t('showing')}{' '}
              {Math.min(currentPage * pageSize + 1, total)}-{Math.min((currentPage + 1) * pageSize, total)}{' '}
              {t('of')} {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Collapsible filters */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={onClearFilters}
        gridCols={3}
      >
        <FilterField label={t('expense.categoryLabel')}>
          <UniversalCombobox
            url="/expense-category"
            labelKey="name"
            placeholder={t('all')}
            queryKey="expenseCategoryFilter"
            selectedId={filters.expenseCategoryId}
            onSelectItem={(item) =>
              onFiltersChange('expenseCategoryId', (item?.id as number | undefined) ?? undefined)
            }
          />
        </FilterField>
        <FilterField label={t('expense.matterLabel')}>
          <UniversalCombobox
            url="/matters"
            labelKey="name"
            placeholder={t('all')}
            queryKey="expenseMatterFilter"
            selectedId={filters.matterId}
            onSelectItem={(item) =>
              onFiltersChange('matterId', (item?.id as number | undefined) ?? undefined)
            }
          />
        </FilterField>
        <FilterField label={t('expense.contractLabel')}>
          <UniversalCombobox
            url="/contracts"
            labelKey="name"
            placeholder={t('all')}
            queryKey="expenseContractFilter"
            selectedId={filters.contractId}
            onSelectItem={(item) =>
              onFiltersChange('contractId', (item?.id as number | undefined) ?? undefined)
            }
          />
        </FilterField>
      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-red-500">
                  {t('expense.loadError')}
                </TableCell>
              </TableRow>
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="hover:bg-[#f8fafc]"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('expense.empty')}</h3>
                    <p className="text-[#64748b]">{t('expense.emptyDesc')}</p>
                    {hasAccess?.('expense', 'create') && onCreate && (
                      <Button className="mt-4" onClick={onCreate}>
                        {t('expense.add')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        leadingControls={<TableZoomControls zoom={tableZoom} />}
        page={currentPage}
        limit={pageSize}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        isLoading={isLoading}
      />
    </div>
  );
}
